import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AudioLines, Navigation } from "lucide-react";

const slides = [
  { img: "/products/ctx-guide.jpg", kicker: "Never lost", title: "Guided safely home.", subtitle: "Vsense walks them home — or to the nearest police station — while you watch over them live from anywhere.", overlay: { img: "/products/parent-car.jpg", name: "Mom", note: "On a break at work", status: "Ethan · en route home" }, speech: "Keep walking straight, then turn right at the next street." },
  { img: "/products/mom-work.jpg", kicker: "Peace of mind at work", title: "Know the moment they move.", subtitle: "Get an instant alert the second your child starts a trip — and follow along live from wherever you are.", notify: { name: "Emily", place: "Publix grocery store" } },
  { img: "/products/ctx-child.jpg", kicker: "For the little explorers", title: "Waiting for the bus. Never alone.", subtitle: "Live streaming, GPS and one-tap SOS — for every step of their day." },
  { img: "/products/ctx-teen.jpg", kicker: "For the free spirits", title: "Freedom to roam.", subtitle: "Two-way voice and a live map mean peace of mind, wherever they go." },
  { img: "/products/ctx-adults.jpg", kicker: "For the ones who raised us", title: "Independence, protected.", subtitle: "\u201cTake me home\u201d navigation and instant emergency help, built in." },
];

export const Hero = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), []);

  useEffect(() => {
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next]);

  const slide = slides[index];

  return (
    <section
      data-testid="hero-section"
      className="relative w-full h-[92vh] min-h-[620px] overflow-hidden bg-black"
    >
      {/* Contextual background scenes (CSS crossfade) */}
      {slides.map((s, i) => (
        <img
          key={i}
          src={s.img}
          alt={s.title}
          aria-hidden={i !== index}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-out"
          style={{ opacity: i === index ? 1 : 0 }}
        />
      ))}

      {/* Scrims for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />

      {/* Parent-watching-live overlay card (per slide) */}
      {slide.overlay && (
        <div
          key={`ov-${index}`}
          data-testid="hero-overlay-card"
          className="hero-reveal absolute z-20 top-20 right-4 md:top-24 md:right-12 w-[248px] rounded-2xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-2xl p-3"
        >
          <div className="flex items-center gap-3">
            <img src={slide.overlay.img} alt={slide.overlay.name} className="w-11 h-11 rounded-xl object-cover shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/50" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-xs font-semibold text-zinc-900 truncate">{slide.overlay.name} · Watching live</span>
              </div>
              <div className="text-[11px] text-zinc-500 truncate">{slide.overlay.note}</div>
            </div>
          </div>
          <div className="mt-2 rounded-lg overflow-hidden relative">
            <img src="/products/app-map.jpg" alt="Live location map" className="w-full h-16 object-cover" />
            <span className="absolute bottom-1 left-2 text-[10px] font-medium text-zinc-800 bg-white/80 px-1.5 py-0.5 rounded">{slide.overlay.status}</span>
          </div>
        </div>
      )}

      {/* Glasses "voice guidance" speech bubble */}
      {slide.speech && (
        <div
          key={`sp-${index}`}
          data-testid="hero-speech-bubble"
          className="hero-reveal absolute z-20 top-24 left-4 right-4 sm:left-8 md:left-auto md:right-[34%] md:top-[24%] md:max-w-xs"
        >
          <div className="relative rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-2xl px-4 py-3 md:max-w-xs">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-lg bg-zinc-900 text-white flex items-center justify-center">
                <AudioLines className="w-3.5 h-3.5" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                Vsense · Voice guidance
              </span>
              <span className="ml-auto flex items-end gap-[3px] h-4">
                {[0, 0.15, 0.3, 0.45, 0.2, 0.35].map((d, i) => (
                  <span key={i} className="eq-bar bg-zinc-900" style={{ animationDelay: `${d}s` }} />
                ))}
              </span>
            </div>
            <p className="text-sm md:text-base font-medium text-zinc-900 leading-snug">
              &ldquo;{slide.speech}&rdquo;
            </p>
            {/* tail pointing toward the wearer */}
            <span className="hidden md:block absolute -right-1.5 top-8 w-4 h-4 rotate-45 bg-white/90 border-r border-b border-white/60" />
          </div>
        </div>
      )}

      {/* Push notification card (per slide) */}
      {slide.notify && (
        <div
          key={`nt-${index}`}
          data-testid="hero-notify-card"
          className="hero-reveal absolute z-20 top-24 left-4 right-4 sm:left-auto sm:right-6 md:top-24 md:right-12 sm:w-[300px]"
        >
          <div className="rounded-2xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-md bg-zinc-900 text-white flex items-center justify-center">
                <Navigation className="w-3.5 h-3.5" />
              </span>
              <span className="text-[11px] font-semibold text-zinc-500">VSENSE</span>
              <span className="ml-auto text-[11px] text-zinc-400">now</span>
            </div>
            <p className="text-sm font-semibold text-zinc-900 leading-snug">
              {slide.notify.name} started a navigation
            </p>
            <p className="text-sm text-zinc-600 leading-snug">
              Destination: {slide.notify.place}
            </p>
            <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/50" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live · following along
            </div>
          </div>
        </div>
      )}

      {/* Content: headline bottom-left, buttons bottom-right */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div key={index} className="max-w-2xl hero-reveal">
            <span className="inline-block text-xs md:text-sm uppercase tracking-[0.3em] font-medium mb-4 px-3 py-1 rounded-full bg-white/90 text-zinc-900">
              {slide.kicker}
            </span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tighter text-white leading-[0.95] drop-shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
              {slide.title}
            </h1>
            <p className="mt-4 text-base md:text-xl text-white/85 max-w-lg leading-relaxed">
              {slide.subtitle}
            </p>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <button
              data-testid="hero-shop-btn"
              onClick={() => navigate("/shop")}
              className="group inline-flex items-center gap-2 bg-white text-zinc-900 text-sm md:text-base font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-100 active:scale-[0.98] transition-all duration-200"
            >
              Join VIP Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </button>
            <a
              href="#features"
              data-testid="hero-learn-btn"
              className="inline-flex items-center border border-white/70 text-white text-sm md:text-base font-medium px-8 py-3.5 rounded-full hover:bg-white hover:text-zinc-900 transition-colors duration-200 backdrop-blur-sm"
            >
              Learn more
            </a>
          </div>
        </div>

        {/* dots */}
        <div className="mt-10 flex items-center gap-3">
          {slides.map((s, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Show ${s.title}`}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{ width: i === index ? 40 : 10, backgroundColor: i === index ? "#ffffff" : "rgba(255,255,255,0.4)" }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
